// src/pages/EmpList.jsx
import React, { useEffect, useState, useMemo } from 'react';
import DataTable from 'react-data-table-component';
import axios from 'axios';
import AppConst from '../apis/appconst';
import NewEmp from '../models/NewEmp';
import DelBox from '../comps/DelBox';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import Swal from 'sweetalert2';

const downloadCsv = (rows = [], filename = 'employees.csv') => {
  if (!rows || rows.length === 0) return;
  const keys = Object.keys(rows[0]);
  const csv = [
    keys.join(','),
    ...rows.map(r => keys.map(k => {
      const val = r[k] === null || r[k] === undefined ? '' : String(r[k]).replace(/"/g, '""');
      return `"${val}"`;
    }).join(','))
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const EmpList = () => {
  const [formData, setFormData] = useState({ l1: [], search_key: '', page_no: 0 });
  const [departments, setDepartments] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [editItem, setEditItem] = useState(null);

  // Modal visibility states
  const [showModal, setShowModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedUserForPassword, setSelectedUserForPassword] = useState(null);
  
  // Password change form state
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // load roles
  const fetchRoles = async () => {
    try {
      const res = await axios.get(AppConst.BASE_URL + AppConst.GetAllRole);
      if (res.data && res.data.status) setDepartments(res.data.list || []);
    } catch (err) {
      console.error('fetchRoles error', err);
      toast.error('Error fetching roles');
    }
  };

  // load users (employees stored as type='user')
  const loadUsers = async () => {
    try {
      const fd = new FormData();
      fd.append('type', 'user');
      fd.append('search_key', formData.search_key || '');
      fd.append('page_no', formData.page_no + '');
      const res = await axios.post(AppConst.BASE_URL + AppConst.getUserListUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        setFormData(prev => ({ ...prev, l1: Array.isArray(res.data.records) ? res.data.records : [] }));
      } else {
        setFormData(prev => ({ ...prev, l1: [] }));
      }
    } catch (err) {
      console.error('loadUsers error', err);
      toast.error('Failed to load users');
      setFormData(prev => ({ ...prev, l1: [] }));
    }
  };

  useEffect(() => {
    const stored = sessionStorage.getItem('ldata');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setIsAdmin(parsed?.role_id === 1 || parsed?.role_id === '1');
      } catch (e) { /* ignore */ }
    }
    fetchRoles();
    loadUsers();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    // reload when search or page changes
    loadUsers();
    // eslint-disable-next-line
  }, [formData.search_key, formData.page_no]);

  const getRoleNameById = (id) => {
    if (!id) return 'Unknown Role';
    const r = departments.find(d => String(d.id) === String(id));
    return r ? r.role_name : 'Unknown Role';
  };

  // OPEN modal for adding new employee
  const openAddModal = () => {
    setEditItem(null);
    setShowModal(true);
    const el = document.getElementById('addbt453');
    if (el) el.click();
  };

  // OPEN modal for editing a row
  const handleEdit = (row) => {
    setEditItem(row);
    setShowModal(true);
    const el = document.getElementById('addbt453');
    if (el) el.click();
  };

  // OPEN change password modal
  const handleChangePassword = (row) => {
    if (!isAdmin) {
      toast.error('Only admin can change passwords.');
      return;
    }
    setSelectedUserForPassword(row);
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setShowCurrentPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setShowPasswordModal(true);
  };

  // SweetAlert2 delete confirmation
  const handleDelete = async (row) => {
    if (!isAdmin) {
      toast.error('Only admin can delete.');
      return;
    }

    const result = await Swal.fire({
      title: `Delete ${row.fname || 'this user'}?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      const fd = new FormData();
      fd.append('user_id', row.user_id + '');
      const res = await axios.post(AppConst.BASE_URL + AppConst.delUserUrl, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (res.data && res.data.status) {
        toast.success('Deleted');
        await loadUsers();
      } else {
        toast.error(res.data?.error || 'Delete failed');
      }
    } catch (err) {
      console.error('delete error', err);
      toast.error('Delete failed');
    }
  };

  // PDF export (Name, Department, Email, Mobile, Password, DOB)
  const exportPdf = () => {
    const rows = filteredRows;
    const doc = new jsPDF('p', 'pt', 'a4');
    doc.setFontSize(14);
    doc.text('Employee List', 40, 40);

    const head = [['Sl. No.', 'Name', 'Department', 'Email', 'Mobile', 'Password', 'DOB']];
    const body = rows.map((r, idx) => {
      const name = r.fname || '';
      const dept = getRoleNameById(r.department);
      const email = r.email || '';
      const mobile = r.mobile || '';
      // Use decrypted password if available, otherwise show encrypted status
      const password = r.password_display || r.password_plain || (r.password ? 'Encrypted' : 'N/A');
      const dob = r.dob || '';
      return [String(idx + 1), name, dept, email, mobile, password, dob];
    });

    autoTable(doc, {
      startY: 60,
      head,
      body,
      styles: { fontSize: 9, cellPadding: 3 },
      headStyles: { fillColor: [215, 100, 34] },
      margin: { left: 30, right: 30 },
      theme: 'grid',
    });

    doc.save(`employees_${Date.now()}.pdf`);
  };

  // client-side filtered rows
  const filteredRows = useMemo(() => {
    const s = (formData.search_key || '').trim().toLowerCase();
    if (!s) return formData.l1 || [];
    return (formData.l1 || []).filter(r =>
      (r.fname || '').toString().toLowerCase().includes(s) ||
      (r.lname || '').toString().toLowerCase().includes(s) ||
      (r.email || '').toString().toLowerCase().includes(s) ||
      (r.mobile || '').toString().toLowerCase().includes(s) ||
      (`SDC000101${r.user_id}` || '').toLowerCase().includes(s)
    );
  }, [formData.l1, formData.search_key]);

  // columns with Change Password button
  const columns = useMemo(() => ([
    {
      name: 'Sl. No.',
      selector: (row, idx) => idx + 1,
      width: '80px',
    },
    { name: 'Name', selector: row => row.fname || '', sortable: true, grow: 1 },
    { name: 'Department', selector: row => getRoleNameById(row.department), sortable: true, width: '180px' },
    { name: 'Email', selector: row => row.email || '', sortable: true, width: '220px' },
    { name: 'Mobile', selector: row => row.mobile || '', width: '140px' },
    { name: 'DOB', selector: row => row.dob || '', width: '120px' },
    {
      name: 'Action',
      cell: row => (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <button 
            className="btn btn-sm btn-outline-primary" 
            onClick={() => handleEdit(row)} 
            disabled={!isAdmin} 
            style={{ opacity: !isAdmin ? 0.5 : 1 }}
            title="Edit Employee"
          >
            Edit
          </button>
          <button 
            className="btn btn-sm btn-outline-warning" 
            onClick={() => handleChangePassword(row)} 
            disabled={!isAdmin} 
            style={{ opacity: !isAdmin ? 0.5 : 1 }}
            title="Change Password"
          >
            <i className="ri-lock-password-line"></i>
          </button>
          <button 
            className="btn btn-sm btn-outline-danger" 
            onClick={() => handleDelete(row)} 
            disabled={!isAdmin} 
            style={{ opacity: !isAdmin ? 0.5 : 1 }}
            title="Delete Employee"
          >
            Delete
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '220px'
    }
  ]), [departments, isAdmin, filteredRows]);

  const customStyles = {
    headCells: { style: { backgroundColor: '#f1f5fb', color: '#405189', fontSize: '14px', fontWeight: '700' } },
    rows: { style: { minHeight: '56px' } },
    cells: { style: { paddingLeft: '12px', paddingRight: '12px' } }
  };

  // Callback when NewEmp closes/finishes adding/updating
  const onNewEmpClose = (refresh = false) => {
    setShowModal(false);
    setEditItem(null);
    if (refresh) loadUsers();
  };

  // Handle password change form submission
  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Validation - current password only required for non-admins
    if (!isAdmin && !passwordData.current_password) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Field',
        text: 'Please enter current password',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!passwordData.new_password) {
      Swal.fire({
        icon: 'warning',
        title: 'Required Field',
        text: 'Please enter new password',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (passwordData.new_password.length < 6) {
      Swal.fire({
        icon: 'warning',
        title: 'Invalid Password',
        text: 'New password must be at least 6 characters',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      Swal.fire({
        icon: 'error',
        title: 'Password Mismatch',
        text: 'New password and confirm password do not match',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Only check if new password is same as current for non-admins (who must enter current password)
    if (!isAdmin && passwordData.current_password === passwordData.new_password) {
      Swal.fire({
        icon: 'warning',
        title: 'Same Password',
        text: 'New password must be different from current password',
        confirmButtonText: 'OK'
      });
      return;
    }

    setPasswordLoading(true);

    try {
      // Get admin's user_id from session storage
      const stored = sessionStorage.getItem('ldata');
      let adminUserId = null;
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          adminUserId = parsed?.user_id;
        } catch (e) { /* ignore */ }
      }

      const fd = new FormData();
      fd.append('user_id', selectedUserForPassword.user_id);
      fd.append('new_password', passwordData.new_password);
      
      // If admin is making the change, send admin_user_id
      if (isAdmin && adminUserId) {
        fd.append('admin_user_id', adminUserId);
      }
      
      // Always send current_password if provided (even for admins)
      if (passwordData.current_password) {
        fd.append('current_password', passwordData.current_password);
      }

      const res = await axios.post(
        AppConst.BASE_URL + AppConst.ChangePassword,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (res.data && res.data.status) {
        // Success - Close modal first, then show success alert
        setShowPasswordModal(false);
        setSelectedUserForPassword(null);
        setPasswordData({
          current_password: '',
          new_password: '',
          confirm_password: ''
        });

        await Swal.fire({
          icon: 'success',
          title: 'Password Changed!',
          text: res.data.success || 'Password has been changed successfully',
          confirmButtonText: 'OK',
          timer: 3000,
          timerProgressBar: true
        });

        // Reload users to get updated data
        await loadUsers();
      } else {
        // Error from backend
        await Swal.fire({
          icon: 'error',
          title: 'Failed',
          text: res.data?.success || 'Failed to change password',
          confirmButtonText: 'OK'
        });
      }
    } catch (err) {
      console.error('Change password error:', err);
      await Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'An error occurred while changing password. Please try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      setPasswordLoading(false);
    }
  };

  // Callback when ChangePassword modal closes
  const onPasswordModalClose = () => {
    setShowPasswordModal(false);
    setSelectedUserForPassword(null);
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="card" id="orderList">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="card-title mb-0">Employees</h5>
            <div>
              <button className="btn btn-success me-2" onClick={openAddModal}>
                <i className="ri-add-line align-bottom me-1" /> Add Employee
              </button>
              <button className="btn btn-outline-secondary me-2" onClick={() => downloadCsv(filteredRows, 'employees.csv')}>Export CSV</button>
              <button className="btn btn-outline-primary" onClick={exportPdf}>Export PDF</button>
            </div>
          </div>

          <div className="card-body border border-dashed border-end-0 border-start-0">
            <div className="row g-2 mb-2">
              <div className="col-md-4">
                <input className="form-control" placeholder="Search by name, email, contact..." type="text" value={formData.search_key}
                  onChange={(e) => setFormData(prev => ({ ...prev, search_key: e.target.value }))} />
              </div>
            </div>
          </div>

          <div className="card-body pt-0">
            <div className="table-responsive table-card mb-1">
              <DataTable
                columns={columns}
                data={filteredRows}
                customStyles={customStyles}
                noHeader
                pagination
                paginationPerPage={10}
                paginationRowsPerPageOptions={[10, 20, 50]}
                highlightOnHover
                persistTableHead
              />
            </div>

            {/* NewEmp modal */}
            <NewEmp
              item={editItem}
              show={showModal}
              onClose={(refresh) => onNewEmpClose(refresh)}
              onAddDepart={() => { setFormData(prev => ({ ...prev, page_no: 0 })); loadUsers(); }}
            />

            {/* Change Password Modal */}
            {showPasswordModal && (
              <>
                {/* Backdrop */}
                <div 
                  className="modal-backdrop fade show" 
                  style={{ zIndex: 1040 }}
                  onClick={onPasswordModalClose}
                ></div>

                {/* Modal */}
                <div 
                  className="modal fade show" 
                  style={{ display: 'block', zIndex: 1050 }}
                  tabIndex="-1"
                >
                  <div className="modal-dialog modal-dialog-centered">
                    <div className="modal-content">
                      <div className="modal-header bg-light">
                        <h5 className="modal-title">
                          Change Password - {selectedUserForPassword?.fname || 'User'}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={onPasswordModalClose}
                          disabled={passwordLoading}
                        ></button>
                      </div>

                      <form onSubmit={handlePasswordSubmit}>
                        <div className="modal-body">
                          {/* Show info message for admin */}
                          {isAdmin && (
                            <div className="alert alert-info mb-3" role="alert">
                              <i className="ri-information-line me-2"></i>
                              As an admin, you can change the password directly without entering the current password.
                            </div>
                          )}

                          {/* Current Password field - only show for non-admins */}
                          {!isAdmin && (
                            <div className="mb-3">
                              <label className="form-label">
                                Current Password <span className="text-danger">*</span>
                              </label>
                              <div className="input-group">
                                <input
                                  type={showCurrentPassword ? "text" : "password"}
                                  className="form-control"
                                  value={passwordData.current_password}
                                  onChange={(e) => setPasswordData(prev => ({ ...prev, current_password: e.target.value }))}
                                  placeholder="Enter current password"
                                  disabled={passwordLoading}
                                  autoComplete="current-password"
                                />
                                <button
                                  className="btn btn-outline-secondary"
                                  type="button"
                                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                  disabled={passwordLoading}
                                >
                                  <i className={`ri-eye${showCurrentPassword ? '-off' : ''}-line`}></i>
                                </button>
                              </div>
                            </div>
                          )}

                          <div className="mb-3">
                            <label className="form-label">
                              New Password <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <input
                                type={showNewPassword ? "text" : "password"}
                                className="form-control"
                                value={passwordData.new_password}
                                onChange={(e) => setPasswordData(prev => ({ ...prev, new_password: e.target.value }))}
                                placeholder="Enter new password (min 6 characters)"
                                disabled={passwordLoading}
                                autoComplete="new-password"
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => setShowNewPassword(!showNewPassword)}
                                disabled={passwordLoading}
                              >
                                <i className={`ri-eye${showNewPassword ? '-off' : ''}-line`}></i>
                              </button>
                            </div>
                            <small className="text-muted">
                              Password must be at least 6 characters
                            </small>
                          </div>

                          <div className="mb-3">
                            <label className="form-label">
                              Confirm New Password <span className="text-danger">*</span>
                            </label>
                            <div className="input-group">
                              <input
                                type={showConfirmPassword ? "text" : "password"}
                                className="form-control"
                                value={passwordData.confirm_password}
                                onChange={(e) => setPasswordData(prev => ({ ...prev, confirm_password: e.target.value }))}
                                placeholder="Re-enter new password"
                                disabled={passwordLoading}
                                autoComplete="new-password"
                              />
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                disabled={passwordLoading}
                              >
                                <i className={`ri-eye${showConfirmPassword ? '-off' : ''}-line`}></i>
                              </button>
                            </div>
                          </div>
                        </div>

                        <div className="modal-footer">
                          <button
                            type="button"
                            className="btn btn-light"
                            onClick={onPasswordModalClose}
                            disabled={passwordLoading}
                          >
                            Cancel
                          </button>
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={passwordLoading}
                          >
                            {passwordLoading ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Changing...
                              </>
                            ) : (
                              <>
                                <i className="ri-lock-password-line me-1"></i>
                                Change Password
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* legacy hidden button retained for compatibility */}
            <button id="addbt453" style={{ display: 'none' }} type="button" className="btn btn-success add-btn" data-bs-toggle="modal" data-bs-target="#showModal"></button>

            <DelBox />
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmpList;